import React, { useMemo } from "react";
import { AccountWithTransactions } from "@common/dto/reports/get-account-transactions-report-response.dto";
import { flatReportAccountSortComparator } from "@common/helpers/accounting";
import { GeneralLedgerView } from "@common/types/views/generalLedgerView";
import { ExpandedState, OnChangeFn } from "@tanstack/react-table";
import { ReportPresentationChrome } from "@common/schemas/domains/reportSettings/report-presentation.schema";
import { NumberFormatValues } from "@common/schemas/domains/reportSettings/number-format-setting.schema";
import { ViewConfig } from "@lib/views";
import { useShowAccountNumbers } from "@hooks/useShowAccountNumbers";
import { CardSection } from "@design-system/Card/CardSection";
import { Card } from "@design-system/Card/Card";
import { AccountingReportHeader } from "../AccountingReportHeader";
import { AccountingReportFooter } from "../AccountingReportFooter";
import { AccountingReportFilters } from "../types";
import {
    AccountingReportContext,
    useAccountingReportContextValue,
} from "../AccountingReport.context";
import { useReportChrome } from "../useReportChrome";
import { GeneralLedgerTable } from "./GeneralLedgerTable";
import { GlTransactionsDrilldownConfig } from "./glTransactionsDrilldown";
import { GLViewRow } from "./glViewTypes";
import styles from "./GeneralLedgerPageContent.module.scss";

interface Props {
    data: AccountWithTransactions[];
    filters: AccountingReportFilters;
    chrome: ReportPresentationChrome;
    numberFormat: NumberFormatValues;
    generationDate: Date;
    expanded: ExpandedState;
    onExpandedChange: OnChangeFn<ExpandedState>;
    view: GeneralLedgerView;
    onViewChange?: (view: GeneralLedgerView) => void;
    viewConfig?: ViewConfig<GLViewRow, string, never>;
    openJournalEntriesInPlace?: boolean;
    transactionsDrilldown?: GlTransactionsDrilldownConfig;
    emptyView?: () => React.ReactNode;
    footer?: React.ReactNode;
}

export const GeneralLedgerPageContent: React.FC<Props> = ({
    data,
    filters,
    chrome,
    numberFormat,
    generationDate,
    expanded,
    onExpandedChange,
    view,
    onViewChange,
    viewConfig,
    openJournalEntriesInPlace,
    transactionsDrilldown,
    emptyView,
    footer,
}) => {
    const showAccountNumbers = useShowAccountNumbers();
    const resolvedChrome = useReportChrome({
        chrome,
        generationDate,
    });

    const sortedData = useMemo(
        () =>
            [...data].sort((a, b) =>
                flatReportAccountSortComparator(
                    a.account,
                    b.account,
                    showAccountNumbers,
                ),
            ),
        [data, showAccountNumbers],
    );
    const accountingReportContextValue = useAccountingReportContextValue(
        filters,
        numberFormat,
    );

    return (
        <Card stacked sharpEdges fullPage>
            <AccountingReportContext.Provider
                value={accountingReportContextValue}
            >
                <CardSection className={styles.header}>
                    <AccountingReportHeader chrome={resolvedChrome} />
                </CardSection>
                <GeneralLedgerTable
                    data={sortedData}
                    expanded={expanded}
                    onExpandedChange={onExpandedChange}
                    view={view}
                    onViewChange={onViewChange}
                    viewConfig={viewConfig}
                    openJournalEntriesInPlace={openJournalEntriesInPlace}
                    transactionsDrilldown={transactionsDrilldown}
                    emptyView={emptyView}
                    footer={footer}
                />
                <AccountingReportFooter chrome={resolvedChrome} />
            </AccountingReportContext.Provider>
        </Card>
    );
};
